<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('dailyExpense.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('dailyExpense.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="d-flex ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">{{
              $t('add_new')
            }}</ts-button>
            <a-range-picker
              v-model="dateRange"
              :ranges="{
                'This Month': [moment().startOf('M'), moment().endOf('month')]
              }"
              @change="fetchData()"
              class="ts-w-64"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
            />
          </div>
          <div class="d-flex">
            <ts-input
              v-tooltip="$t('dailyExpense.search')"
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
          </div>
        </div>
      </ts-card-wrapper>
      <Table
        :columns="columns"
        :data="resources"
        size="small"
        :loading="loading"
        highlight-row
        stripe
        max-height="700"
      >
          <template slot-scope="{ row }" slot="expCategory">
            {{
              row.expense_item.category
                ? row.expense_item.category.exp_category
                : null
            }}
          </template>
          <template slot-scope="{ row }" slot="expItem">
            {{ row.expense_item.exp_item }}
          </template>
          <template slot-scope="{ row }" slot="unitPrice">
            <div class="text-right ts-text-blue-700 ts-font-semibold">
              {{
                row.currency.currency_code +
                ' ' +
                $formatNumber(row.exp_unit_price, 2)
              }}
            </div>
          </template>
          <template slot-scope="{ row }" slot="expAmount">
            <div class="text-right ts-text-blue-700 ts-font-semibold">
              {{
                row.currency.currency_code +
                ' ' +
                $formatNumber(row.exp_amount, 2)
              }}
            </div>
          </template>
          <template slot-scope="{ row }" slot="fxRate">
            <div class="text-center ts-text-blue-700 ts-font-semibold">
              {{ row.fx_rate }}
            </div>
          </template>
          <template slot-scope="{ row }" slot="userName">
            {{ row.user ? row.user.name : '' }}
          </template>
          <template slot-scope="{ row }" slot="action">
            <a
              href="#"
              v-tooltip="$t('dailyCashAdvance.reprint')"
              class="text-primary text-red mr-2"
              @click.prevent="reprintReport(row)"
            >
              <i class="fas fa-file-pdf"></i>
            </a>
            <a
              href="#"
              v-tooltip="$t('edit')"
              class="text-primary mr-2"
              @click.prevent="edit(row)"
            >
              <i class="fas fa-edit"></i>
            </a>
            <a-popconfirm
              placement="leftTop"
              :title="$t('are_you_sure_to_delete')"
              :ok-text="$t('yes')"
              :cancel-text="$t('no')"
              @confirm="deleteRecord(row)"
            >
              <a href="#" :disabled="row._deleting">
                <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
                <i class="fa fa-trash text-red" v-else></i>
              </a>
            </a-popconfirm>
          </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
      <!-- form-action -->
      <a-modal
        v-if="showForm"
        v-model="showForm"
        :title="$t('dailyExpense.pageTitle')"
        centered
        @cancel="clearEdit"
        :zIndex="1020"
        :footer="null"
        width="550px"
      >
        <from-action @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
      <a-modal :title="$t('pdf')" width="50%" v-model="show_pdf" :footer="null">
        <ts-preview-pdf v-model="waiting" :src="src" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FromAction from './form'
import moment from 'moment'
import { Errors } from 'form-backend-validation'

export default {
  name: 'dailyExpenseIndex',
  components: {
    FromAction
  },
  data () {
    return {
      loading: false,
      waiting: false,
      showForm: false,
      show_pdf: false,
      src: '',
      errors: new Errors()
    }
  },
  created () {
    this.fetchData()
  },
  computed: {
    ...mapState('expense/dailyExpense', ['resources', 'pagination']),
    search: {
      get () {
        return this.$store.state.expense.dailyExpense.search
      },
      set (newValue) {
        this.$store.commit('expense/dailyExpense/SET_SEARCH', newValue)
        this.$store.commit('expense/dailyExpense/RESET_CURRENT_PAGE')
      }
    },
    dateRange: {
      get () {
        return this.$store.state.expense.dailyExpense.dateRange
      },
      set (newValue) {
        this.$store.commit('expense/dailyExpense/SET_DATE_RANGE', newValue)
      }
    },
    columns () {
      return [
        { 
          title: this.$t('dailyExpense.date'),
          key: 'exp_date' 
        },
        { 
          title: this.$t('dailyExpense.category'),
          slot: 'expCategory' 
        },
        { 
          title: this.$t('dailyExpense.expenseItem'),
          slot: 'expItem'  
        },
        { 
          title: this.$t('dailyExpense.unitPrice'),
          slot: 'unitPrice', 
          align: 'right'
        },
        { 
          title: this.$t('dailyExpense.quantity'),
          key: 'exp_qty', 
          align: 'center'
        },
        { 
          title: this.$t('dailyExpense.amount'),
          slot: 'expAmount', 
          align: 'right'
        },
        { 
          title: this.$t('dailyExpense.fxRate'),
          slot: 'fxRate', 
          align: 'center'
        },
        { 
          title: this.$t('dailyExpense.expenseBy'),
          key: 'employee_name_en' 
        },
        {
          title: this.$t('dailyCashAdvance.vendorName'),
          key: 'vendor_name'
        },
        { 
          title: this.$t('dailyExpense.createdBy'),
          slot: 'userName'  
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 120
        }
      ]
    }
  },
  methods: {
    moment,
    fetchData (attributes) {
      this.loading = true
      this.$store
        .dispatch('expense/dailyExpense/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew () {
      this.showForm = true
    },
    edit (record) {
      this.$store.commit('expense/dailyExpense/SET_EDIT_DATA', record)
      this.showForm = true
    },
    clearEdit () {
      this.$store.commit('expense/dailyExpense/SET_EDIT_DATA', {})
      this.fetchData()
      this.showForm = false
    },
    deleteRecord (record) {
      record._deleting = true
      this.$store
        .dispatch('expense/dailyExpense/destroy', record.tran_id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    reprintReport (record) {
      this.show_pdf = true
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('expense/dailyExpense/toPdf', record.tran_id)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave (to, from, next) {
    this.$store.commit('expense/dailyExpense/RESET_STATE')
    next()
  }
}
</script>
